import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { saveText } from '../../utils/download';
import { canShareNatively, copyText, shareNatively } from '../../utils/share';
import * as S from '../specimens/styles';

/*
 * One "Share" button for a page or a record (2026-10-19): it opens a short
 * menu of what can be done with it --
 *
 *   Copy link        `url`, a page someone with access opens (never a secret
 *                    beyond what they would see there)
 *   Share…           the device's share sheet (phones), with title, text, url
 *   Copy as text     `text`, a plain summary, and the link
 *   Download …       each of `exports`: {key, label, filename, mime, content()}
 *   Print            `onPrint`, when the page prints itself
 *
 * Nothing is sent anywhere by the app: the person chooses where it goes. The
 * menu closes on Escape or a click outside, and says what happened ("Link
 * copied") in a polite live region.
 */
export default function ShareMenu({ title, text, url, exports = [], onPrint, label, testid = 'share' }) {
  const { t } = useTranslation('common');
  const [open, setOpen] = useState(false);
  const [said, setSaid] = useState('');
  const box = useRef(null);
  const button = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const outside = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    const key = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener('mousedown', outside);
    document.addEventListener('keydown', key);
    return () => {
      document.removeEventListener('mousedown', outside);
      document.removeEventListener('keydown', key);
    };
  }, [open]);

  const done = (message) => {
    setSaid(message);
    setOpen(false);
    setTimeout(() => setSaid(''), 2500);
  };
  const copy = async (value, ok) => done((await copyText(value)) ? ok : t('share.copyFailed'));
  const native = async () => {
    const result = await shareNatively({ title, text, url });
    if (result === 'shared') done(t('share.shared'));
    else if (result === 'unavailable') copy(url || text, t('share.linkCopied'));
    else setOpen(false);
  };

  return (
    <span ref={box} style={{ position: 'relative', display: 'inline-block' }} data-testid={testid}>
      <button ref={button} type="button" style={S.ghostBtn} aria-haspopup="true" aria-expanded={open}
        onClick={() => setOpen(!open)} title={t('share.tip')} data-testid={`${testid}-button`}>
        {label || t('share.button')}
      </button>
      {open && (
        <div role="group" aria-label={t('share.menu')} style={menu} data-testid={`${testid}-menu`}>
          {url && (
            <button type="button" style={item} onClick={() => copy(url, t('share.linkCopied'))}
              data-testid={`${testid}-copy-link`}>{t('share.copyLink')}</button>
          )}
          {canShareNatively() && (url || text) && (
            <button type="button" style={item} onClick={native} data-testid={`${testid}-native`}>
              {t('share.native')}</button>
          )}
          {text && (
            <button type="button" style={item} onClick={() => copy(url ? `${text}\n${url}` : text, t('share.textCopied'))}
              data-testid={`${testid}-copy-text`}>{t('share.copyText')}</button>
          )}
          {exports.map((x) => (
            <button key={x.key} type="button" style={item} data-testid={`${testid}-export-${x.key}`}
              onClick={async () => {
                try {
                  saveText(await x.content(), x.filename, x.mime);
                  done(t('share.downloaded'));
                } catch {
                  done(t('share.exportFailed'));
                }
              }}>{x.label}</button>
          ))}
          {onPrint && (
            <button type="button" style={item} onClick={() => { setOpen(false); onPrint(); }}
              data-testid={`${testid}-print`}>{t('share.print')}</button>
          )}
        </div>
      )}
      <span role="status" aria-live="polite" style={said ? status : hidden} data-testid={`${testid}-status`}>{said}</span>
    </span>
  );
}

const menu = {
  position: 'absolute', insetInlineStart: 0, top: '100%', marginTop: 4, zIndex: 1000, minWidth: 200,
  display: 'grid', gap: 2, padding: 6, borderRadius: 8, border: '1px solid var(--c-border)',
  background: 'var(--c-surface, #fff)', boxShadow: '0 6px 20px rgba(0,0,0,0.15)',
};
const item = { ...S.ghostBtn, textAlign: 'start', border: 'none', width: '100%' };
const status = { marginInlineStart: 8, fontSize: 13, color: 'var(--c-success-fg, inherit)' };
const hidden = { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' };
